import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  MessageCircle,
  X,
  Send,
  Bot,
  User,
  Loader2,
  Mail,
  Minimize2,
  Maximize2
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AIChatWidget = ({ context = 'landing', user = null }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showEscalation, setShowEscalation] = useState(false);
  const [escalationEmail, setEscalationEmail] = useState(user?.email || '');
  const [escalationName, setEscalationName] = useState(user?.name || '');
  const [escalationSummary, setEscalationSummary] = useState('');
  const [escalationCategory, setEscalationCategory] = useState('more_help_needed');
  const [escalationSent, setEscalationSent] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Initial greeting
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const greeting = context === 'landing' 
        ? "Hi! I'm ClockIn Pro's AI assistant. I can help you learn about our time tracking solution, pricing, and features. What would you like to know?"
        : `Hi${user?.name ? ` ${user.name}` : ''}! I'm here to help with any questions about ClockIn Pro. How can I assist you today?`;
      
      setMessages([{
        role: 'assistant',
        content: greeting,
        timestamp: new Date().toISOString()
      }]);
    }
  }, [isOpen, context, user?.name, messages.length]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && !isMinimized) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, isMinimized]);

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputValue.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const headers = {};
      const token = localStorage.getItem('token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await axios.post(`${API}/api/support/chat`, {
        message: userMessage.content,
        session_id: sessionId,
        context: context
      }, { headers });

      setSessionId(response.data.session_id);

      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Check if escalation is suggested
      if (response.data.needs_escalation) {
        setShowEscalation(true);
        // Auto-generate summary from the last user message
        setEscalationSummary(userMessage.content);
      }
      
      // Track failed attempts (when AI couldn't fully resolve)
      if (response.data.response.toLowerCase().includes("i'm not sure") || 
          response.data.response.toLowerCase().includes("contact support") ||
          response.data.response.toLowerCase().includes("i don't have")) {
        setFailedAttempts(prev => prev + 1);
        // Offer escalation after 2 failed attempts
        if (failedAttempts >= 1) {
          setShowEscalation(true);
          setEscalationSummary(userMessage.content);
        }
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I'm sorry, I'm having trouble connecting right now. Please try again in a moment, or email us at support@clockinpro.live",
        timestamp: new Date().toISOString(),
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEscalate = async () => {
    if (!escalationEmail.trim() || !escalationName.trim()) return;

    setIsLoading(true);
    try {
      const headers = {};
      const token = localStorage.getItem('token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await axios.post(`${API}/api/support/escalate-ticket`, {
        session_id: sessionId,
        user_email: escalationEmail,
        user_name: escalationName,
        issue_summary: escalationSummary || 'Support request from chat',
        category: escalationCategory
      }, { headers });

      setEscalationSent(true);
      setShowEscalation(false);
      
      const ticketNumber = response.data.ticket_number || response.data.ticket_id?.slice(0, 8).toUpperCase();
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Thanks! I've created a support ticket (#${ticketNumber}) and sent it to our team. They'll get back to you at ${escalationEmail} as soon as possible.`,
        timestamp: new Date().toISOString()
      }]);
    } catch (error) {
      console.error('Escalation error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isOpen) {
    return (
      <Button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-20 right-6 w-14 h-14 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 shadow-lg shadow-indigo-200 z-[9999]"
        data-testid="chat-widget-button"
      >
        <MessageCircle className="w-6 h-6" />
      </Button>
    );
  }

  return (
    <Card className={`fixed bottom-20 right-6 z-[9999] shadow-2xl border-indigo-200 transition-all duration-300 ${
      isMinimized ? 'w-72 h-14' : 'w-96 h-[500px]'
    }`}>
      {/* Header */}
      <CardHeader className="p-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-t-lg flex flex-row items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
            <Bot className="w-5 h-5" />
          </div>
          <div>
            <CardTitle className="text-sm font-medium">ClockIn Pro Support</CardTitle>
            {!isMinimized && (
              <p className="text-xs text-indigo-100">AI-powered assistance</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-white hover:bg-white/20"
            onClick={() => setIsMinimized(!isMinimized)}
          >
            {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-white hover:bg-white/20"
            onClick={() => setIsOpen(false)}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>

      {!isMinimized && (
        <CardContent className="p-0 flex flex-col h-[calc(100%-56px)]">
          {/* Messages */}
          <ScrollArea className="flex-1 p-4">
            <div className="space-y-4">
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {msg.role === 'assistant' && (
                    <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 text-indigo-600" />
                    </div>
                  )}
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${
                      msg.role === 'user'
                        ? 'bg-indigo-600 text-white rounded-br-md'
                        : msg.isError
                        ? 'bg-red-50 text-red-700 border border-red-200 rounded-bl-md'
                        : 'bg-slate-100 text-slate-700 rounded-bl-md'
                    }`}
                  >
                    {msg.content}
                  </div>
                  {msg.role === 'user' && (
                    <div className="w-7 h-7 rounded-full bg-indigo-600 flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>
              ))}
              {isLoading && (
                <div className="flex gap-2 justify-start">
                  <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center">
                    <Bot className="w-4 h-4 text-indigo-600" />
                  </div>
                  <div className="bg-slate-100 rounded-2xl rounded-bl-md px-4 py-2">
                    <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          {/* Escalation Form */}
          {showEscalation && !escalationSent && (
            <div className="p-3 bg-amber-50 border-t border-amber-200">
              <p className="text-xs text-amber-700 mb-2 flex items-center gap-1">
                <Mail className="w-3 h-3" />
                Create a support ticket
              </p>
              <Input
                type="text"
                placeholder="Your name"
                value={escalationName}
                onChange={(e) => setEscalationName(e.target.value)}
                className="mb-2 text-sm h-8"
                data-testid="escalation-name"
              />
              <Input
                type="email"
                placeholder="Your email address"
                value={escalationEmail}
                onChange={(e) => setEscalationEmail(e.target.value)}
                className="mb-2 text-sm h-8"
                data-testid="escalation-email"
              />
              <Input
                placeholder="Describe your issue"
                value={escalationSummary}
                onChange={(e) => setEscalationSummary(e.target.value)}
                className="mb-2 text-sm h-8"
                data-testid="escalation-summary"
              />
              <select
                id="escalation-category"
                name="escalation_category"
                value={escalationCategory}
                onChange={(e) => setEscalationCategory(e.target.value)}
                className="w-full mb-2 text-sm h-8 px-2 rounded-md border border-gray-300 bg-white"
                data-testid="escalation-category"
              >
                <option value="more_help_needed">I need more help</option>
                <option value="bug">Report a bug</option>
                <option value="system_improvement">Suggest improvement</option>
                <option value="development_suggestion">Request a feature</option>
              </select>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleEscalate}
                  disabled={!escalationEmail.trim() || !escalationName.trim() || isLoading}
                  className="flex-1 h-7 text-xs bg-amber-600 hover:bg-amber-700"
                  data-testid="submit-ticket-btn"
                >
                  Create Ticket
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowEscalation(false)}
                  className="h-7 text-xs"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-3 border-t">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                placeholder="Type your message..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isLoading}
                className="flex-1"
                data-testid="chat-input"
              />
              <Button
                onClick={sendMessage}
                disabled={!inputValue.trim() || isLoading}
                size="icon"
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="chat-send-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-[10px] text-slate-400 mt-2 text-center">
              Powered by AI • support@clockinpro.live
            </p>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default AIChatWidget;
